import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {deprecatedTrackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {INSIGHTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {isRegistrySupportedType} from 'constants/RegistryConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import AnalyticsDashboardDetails from 'insights/analytics-dashboards/details/AnalyticsDashboardDetails';
import {getAnalyticsDashboardViewStateId} from 'insights/analytics-dashboards/details/view-state/AnalyticsDashboardViewState';
import {ViewType} from 'insights/analytics-dashboards/details/view-state/ViewStateTypes';
import {GetBioentityAnalyticsDashboardQuery} from 'insights/embeddings/data/__generated/GetBioentityAnalyticsDashboardQuery';
import parameterValueTemplatesToOverrides from 'insights/embeddings/data/parameterValueTemplatesToOverrides';
import {BlobLinkPreviewTabProvider} from 'notebook/data-tables/BlobLinkPreviewTabContext';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import useOnMount from 'ui-library/hooks/useOnMount';
import {PaneBorder} from 'ui-library/pane/Pane';
import {TabLayoutBackgroundColor} from 'ui-library/TabLayout';
import APIUtil from 'util/APIUtil';

type AnalyticsDashboardLink = NonNullable<
  NonNullable<
    ExtractByTypename<GetBioentityAnalyticsDashboardQuery['bioentity'], 'BasicFolderItemNode'>['tagSchema']
  >['analyticsDashboardLink']
>;

export interface EmbeddedAnalyticsDashboardViewProps {
  analyticsDashboardLink: AnalyticsDashboardLink;
  itemId: string;
}

export default function EmbeddedAnalyticsDashboardView(
  props: EmbeddedAnalyticsDashboardViewProps
): JSX.Element {
  const {originalAnalyticsDashboard: embeddedAnalyticsDashboard, parameterValueTemplates} =
    props.analyticsDashboardLink;

  useOnMount(() => {
    const itemType = APIUtil.apiIdToType(props.itemId);
    // For registry embedding we want to know the entityType of the embedded schema. For future types of embeddings,
    // consider defining what metadata you would like, and update this function to match it.
    assert(
      isRegistrySupportedType(itemType),
      `OPENED_EMBEDDED_DASHBOARD_TAB event not implemented for item of type ${itemType}`
    );
    deprecatedTrackEvent(INSIGHTS_ANALYTICS_ACTIONS.OPENED_EMBEDDED_DASHBOARD_TAB, {
      entityType: itemType,
    });
  });

  return (
    <section aria-label="Embedded analytics dashboard view" className={css(styles.wrapper)}>
      {isInaccessible(embeddedAnalyticsDashboard) ? (
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text="You do not have access to the dashboard embedded in this item"
        />
      ) : (
        <BlobLinkPreviewTabProvider fileId={props.itemId}>
          <AnalyticsDashboardDetails
            key={props.itemId}
            analyticsDashboardId={embeddedAnalyticsDashboard.id}
            readOnly={true}
            delayExpensiveRenders={true}
            parameterValueOverrides={parameterValueTemplatesToOverrides(
              parameterValueTemplates,
              props.itemId
            )}
            showReadOnlyBadge={false}
            showDashboardCloneButton={false}
            showDashboardLink={true}
            theme={{dashboardBackgroundColor: TabLayoutBackgroundColor.WHITE, paneBorder: PaneBorder.SOLID}}
            viewStateId={getAnalyticsDashboardViewStateId(embeddedAnalyticsDashboard.id, {
              embeddingItemId: props.itemId,
              viewType: ViewType.EMBEDDED_ANALYTICS_DASHBOARD,
            })}
          />
        </BlobLinkPreviewTabProvider>
      )}
    </section>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    overflowY: 'auto',
  },
});
